import React, { useState } from "react";
import {
  FaChevronDown,
  FaQuestionCircle,
  FaMobileAlt,
  FaStethoscope,
  FaHeart,
  FaShieldAlt,
  FaHeadset,
  FaSearch,
} from "react-icons/fa";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import styles from "./FAQ.module.css";
import ScrollToTop from "../../components/ScrollToTop/ScrollToTop";

interface Question {
  question: string;
  answer: string;
  tags: string[];
}

interface FAQCategory {
  category: string;
  icon: React.ReactNode;
  questions: Question[];
}

const FAQPage: React.FC = () => {
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const faqData: FAQCategory[] = [
    {
      category: "Getting Started",
      icon: <FaMobileAlt />,
      questions: [
        {
          question: "How do I create an account on LicoMeds?",
          answer:
            "Creating an account is simple! Download our mobile app from the App Store or Google Play, then tap 'Sign Up'. You'll need to provide your email, phone number, and create a secure password. We'll verify your phone number with an OTP, and you're ready to start your healthcare journey!",
          tags: ["account", "signup", "registration", "mobile app"],
        },
        {
          question: "Is LicoMeds free to use?",
          answer:
            "Yes! LicoMeds is free to download. Our fee cashback system ensures you earn rewards on transaction fees paid to healthcare providers. While merchants pay transparent platform fees, patients receive up to 50% cashback from these fees that can be used for payments, donations, or withdrawals.",
          tags: ["free", "pricing", "cost", "fee cashback"],
        },
        {
          question: "Which devices support LicoMeds?",
          answer:
            "LicoMeds mobile app works on iOS (iPhone/iPad) devices running iOS 12+ and Android devices running Android 6.0+. All healthcare services happen in the mobile app - the website is for information and donations only.",
          tags: ["devices", "compatibility", "mobile", "app"],
        },
        {
          question:
            "What makes LicoMeds different from other telemedicine apps?",
          answer:
            "LicoMeds is specifically designed for African healthcare needs with a unique fee cashback system, community support features, and doctor-controlled sessions. Our platform integrates doctors, labs, and pharmacies in one ecosystem with transparent fee structures and admin-controlled thresholds.",
          tags: ["unique", "difference", "features", "fee cashback"],
        },
      ],
    },
    {
      category: "Mobile App Features",
      icon: <FaStethoscope />,
      questions: [
        {
          question: "How do I consult with a doctor in the app?",
          answer:
            "Open the mobile app, tap 'Consult Doctor', choose your specialty, and you'll be connected with a verified healthcare professional. Doctors control the session flow and can suspend or end consultations. They can also refer you to specialists or labs seamlessly.",
          tags: ["consultation", "doctor", "appointment", "mobile"],
        },
        {
          question: "How quickly can I speak to a doctor?",
          answer:
            "Most consultations begin within 2-5 minutes! Our doctors are available 24/7 through the mobile app. During peak hours, you might wait up to 10 minutes, but we'll always keep you updated on wait times.",
          tags: ["speed", "waiting time", "24/7", "mobile"],
        },
        {
          question: "Can doctors prescribe medication through the app?",
          answer:
            "Yes! Licensed doctors can issue digital prescriptions through the mobile app. These are legally valid and can be used at any registered pharmacy in our network. The pharmacy integration is seamless within the app ecosystem.",
          tags: ["prescription", "medication", "pharmacy", "mobile"],
        },
        {
          question: "How does the referral system work?",
          answer:
            "Doctors can refer you to specialists or labs directly through the app. Medical information is shared seamlessly, and you'll be notified of the referral. Labs can upload results directly to your app, and doctors can review them for follow-up consultations.",
          tags: ["referral", "specialists", "labs", "integration"],
        },
        {
          question: "What are virtual bank accounts?",
          answer:
            "Each user gets a dedicated virtual bank account for secure transactions within the app. This handles your fee cashback, payments to merchants, and withdrawal requests. Merchants can add up to 3 withdrawal accounts with admin-controlled processing times.",
          tags: ["virtual accounts", "payments", "fee cashback", "security"],
        },
      ],
    },
    {
      category: "Fee Cashback & Payments",
      icon: <FaHeart />,
      questions: [
        {
          question: "How does the fee cashback system work?",
          answer:
            "When you pay platform transaction fees to healthcare providers, you earn cashback automatically from those fees. The fee distribution is: 50% to you, 20% to your referrer (if you used a referral code during signup), and 30% to LicoMeds. If no referral, it's 50% to you and 50% to LicoMeds. Healthcare providers receive their full service fees separately.",
          tags: ["fee cashback", "payments", "rewards", "distribution"],
        },
        {
          question: "When can I withdraw my fee cashback?",
          answer:
            "You can withdraw fee cashback once you reach the minimum threshold set by LicoMeds admin. However, you can use your cashback for donations to help other patients anytime - no minimum required for charitable giving.",
          tags: ["withdrawal", "fee cashback", "minimum", "donations"],
        },
        {
          question: "Can I donate my fee cashback to help others?",
          answer:
            "Absolutely! You can donate your fee cashback to support other patients' medical bills through the app's community section. Donations don't require meeting the withdrawal threshold since it's a charitable action that shouldn't be limited.",
          tags: ["donations", "charity", "help others", "fee cashback"],
        },
        {
          question: "How do platform fees work?",
          answer:
            "Healthcare providers pay transparent platform fees that fund the fee cashback system. All merchants agree to these Terms & Conditions during onboarding. The admin controls withdrawal frequencies and processing times for transparency. These fees are separate from the healthcare provider service charges.",
          tags: ["platform fees", "transparency", "terms", "admin"],
        },
      ],
    },
    {
      category: "Security & Privacy",
      icon: <FaShieldAlt />,
      questions: [
        {
          question: "Is my health information secure in the app?",
          answer:
            "Your privacy is our top priority. The mobile app uses military-grade encryption, complies with international healthcare data protection standards, and is HIPAA-compliant. Health records are encrypted end-to-end and never shared without your explicit consent.",
          tags: ["security", "privacy", "HIPAA", "encryption", "mobile"],
        },
        {
          question: "Who can see my medical records?",
          answer:
            "Only you and the healthcare professionals you're consulting with through the app can access your medical records. You have complete control over who sees what information. We never sell or share your data with third parties.",
          tags: ["medical records", "privacy", "access", "control"],
        },
        {
          question: "Can I delete my account and data?",
          answer:
            "Yes, you have complete control over your data. You can delete your account and all associated health records through the mobile app settings. Once deleted, your data is permanently removed from our servers within 30 days.",
          tags: ["delete account", "data removal", "privacy", "mobile"],
        },
        {
          question: "How do you verify doctor credentials?",
          answer:
            "All healthcare professionals undergo rigorous verification including license validation, background checks, and credential verification with medical boards. We conduct regular reviews and require continuing education compliance.",
          tags: ["doctor verification", "credentials", "safety", "quality"],
        },
      ],
    },
    {
      category: "Support & Technical",
      icon: <FaHeadset />,
      questions: [
        {
          question: "The mobile app isn't working properly. What should I do?",
          answer:
            "First, try closing and reopening the app. If issues persist, check for app updates in your app store. You can contact our 24/7 technical support through the 'Help' section in the app or email our support team.",
          tags: ["technical issues", "app problems", "support", "mobile"],
        },
        {
          question: "Is the mobile app available 24/7?",
          answer:
            "Yes! The mobile app operates 24/7 with doctors available around the clock. Our AI-powered chatbot provides instant responses, and human support is available 24/7 for urgent medical concerns and technical issues.",
          tags: ["24/7", "availability", "support", "mobile"],
        },
        {
          question: "Can I use the app outside Nigeria?",
          answer:
            "Currently, LicoMeds primarily serves Nigeria, but we're expanding across Africa. Nigerian users can access the mobile app services while traveling internationally. We're working on launching in Ghana, Kenya, and South Africa soon!",
          tags: ["international", "travel", "availability", "expansion"],
        },
        {
          question: "What internet speed do I need for video consultations?",
          answer:
            "For optimal video quality in the mobile app, we recommend at least 1 Mbps upload and download speed. However, the app automatically adjusts video quality based on your connection. Even with slower internet, you can still use text chat and voice calls.",
          tags: ["internet speed", "video quality", "requirements", "mobile"],
        },
        {
          question: "Can I donate through the website without the app?",
          answer:
            "Yes! While all healthcare services happen in the mobile app, you can make donations through our website using our secure fintech partner integration. This allows anonymous donors and passersby to support patients in need even without downloading the app.",
          tags: ["website donations", "fintech", "anonymous", "support"],
        },
      ],
    },
  ];

  // Create a flat list of all questions with category info
  const allQuestions = faqData.flatMap((category) =>
    category.questions.map((question, index) => ({
      ...question,
      categoryName: category.category,
      categoryIcon: category.icon,
      id: `${category.category}-${index}`,
    }))
  );

  const filteredQuestions = allQuestions.filter((question) => {
    const matchesSearch =
      question.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      question.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      question.tags.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesCategory =
      activeCategory === "all" ||
      question.categoryName.toLowerCase().replace(/\s+/g, "-") ===
        activeCategory;

    return matchesSearch && matchesCategory;
  });

  const categories = [
    { id: "all", label: "All Questions", icon: <FaQuestionCircle /> },
    ...faqData.map((category) => ({
      id: category.category.toLowerCase().replace(/\s+/g, "-"),
      label: category.category,
      icon: category.icon,
    })),
  ];

  const toggleItem = (id: string) => {
    setActiveItem(activeItem === id ? null : id);
  };

  return (
    <>
      <Navbar />
      <ScrollToTop />

      <div className={styles.faqPage}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <div className={styles.container}>
            <div className={styles.heroContent}>
              <div className={styles.heroIcon}>
                <FaQuestionCircle />
              </div>
              <h1 className={styles.heroTitle}>Frequently Asked Questions</h1>
              <p className={styles.heroSubtitle}>
                Get instant answers about LicoMeds mobile app, consultations,
                fee cashback system, and community support
              </p>

              {/* Search */}
              <div className={styles.searchContainer}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search questions or enter keywords..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Content */}
        <section className={styles.faqContent}>
          <div className={styles.container}>
            {/* Categories */}
            <div className={styles.categories}>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`${styles.categoryBtn} ${
                    activeCategory === category.id ? styles.active : ""
                  }`}
                >
                  {category.icon}
                  <span>{category.label}</span>
                </button>
              ))}
            </div>

            {/* FAQ Items */}
            <div className={styles.faqList}>
              {filteredQuestions.length > 0 ? (
                filteredQuestions.map((question) => (
                  <div
                    key={question.id}
                    className={`${styles.faqItem} ${
                      activeItem === question.id ? styles.active : ""
                    }`}
                  >
                    <button
                      onClick={() => toggleItem(question.id)}
                      className={styles.faqQuestion}
                    >
                      <div className={styles.questionContent}>
                        <div className={styles.questionIcon}>
                          {question.categoryIcon}
                        </div>
                        <span className={styles.questionText}>
                          {question.question}
                        </span>
                      </div>
                      <FaChevronDown
                        className={`${styles.chevron} ${
                          activeItem === question.id ? styles.rotated : ""
                        }`}
                      />
                    </button>

                    {activeItem === question.id && (
                      <div className={styles.faqAnswer}>
                        <p>{question.answer}</p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className={styles.noResults}>
                  <FaSearch className={styles.noResultsIcon} />
                  <h3>No questions found</h3>
                  <p>
                    Try adjusting your search terms or selecting a different
                    category
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default FAQPage;
